<!-- src/Pages/Sites/FormEditor.svelte -->
<script module>
  import AppLayout from "$lib/layouts/AppLayout.svelte";
  export const layout = AppLayout;
</script>

<script lang="ts">
  import { router } from "@inertiajs/svelte";
  import { toast } from "svelte-sonner";
  import { appStore } from "$lib/stores/appStore";
  import { Button } from "$lib/components/ui/button/index.js";

  import {
    Save,
    ArrowLeft,
    Globe,
    Palette,
    Settings,
    Navigation,
    Shield,
    BarChart3,
    Search,
    Plus,
    Trash2,
    Sliders,
    ExternalLink,
    Network,
    Link2,
    Info,
  } from "lucide-svelte";

  // 接收后端传递的数据
  let {
    site_id,
    current_site = {},
    settings_data = {},
    t = {} as any,
  } = $props();

  // 世界主流语言列表
  const WORLD_LANGUAGES = [
    { code: "zh", name: "中文 (Chinese)" },
    { code: "en", name: "English" },
    { code: "ja", name: "日本語 (Japanese)" },
    { code: "ko", name: "한국어 (Korean)" },
    { code: "de", name: "Deutsch (German)" },
    { code: "fr", name: "Français (French)" },
    { code: "es", name: "Español (Spanish)" },
    { code: "it", name: "Italiano (Italian)" },
    { code: "ru", name: "Русский (Russian)" },
    { code: "pt", name: "Português (Portuguese)" },
    { code: "ar", name: "العربية (Arabic)" },
  ];

  // 1. 声明基础默认状态
  let siteBase = $state({
    name: "",
    subdomain: "",
    status: "published",
    position: 0,
    default_locale: "zh",
    supported_locales: ["zh"],
  });

  // 2. 声明设置默认状态（已移除无用的 domains，保留完整的 domain_redirects 和 custom_code）
  let settings = $state({
    appearance: { theme: "light", primary_color: "#3b82f6", favicon_url: "" },
    header: {} as Record<string, any>,
    footer: {} as Record<string, any>,
    seo_metadata: {} as Record<string, any>,
    domain_redirects: {
      primary: "",
      redirect_from: [] as string[],
      redirect_status: 301,
    },
    custom_code: { head: "", body_start: "", body_end: "" },
    analytics: { google_analytics_id: "", baidu_tongji_id: "" },
  });

  // 3. 辅助状态：将 redirect_from (数组) 转为多行文本方便输入框编辑
  let redirectFromText = $state("");

  // 4. UI 状态
  let activeLocale = $state("zh");
  let activeGroup = $state("basic");

  // 💡 使用 $effect.pre 在 DOM 渲染前同步后端传入的数据
  $effect.pre(() => {
    siteBase.name = current_site.name || "";
    siteBase.subdomain = current_site.subdomain || "";
    siteBase.status = current_site.status || "published";
    siteBase.position = current_site.position || 0;
    siteBase.default_locale = current_site.default_locale || "zh";
    siteBase.supported_locales = current_site.supported_locales || ["zh"];

    settings.appearance = settings_data.appearance || {
      theme: "light",
      primary_color: "#3b82f6",
      favicon_url: "",
    };
    settings.header = settings_data.header || {};
    settings.footer = settings_data.footer || {};
    settings.seo_metadata = settings_data.seo_metadata || {};
    settings.domain_redirects = settings_data.domain_redirects || {
      primary: "",
      redirect_from: [],
      redirect_status: 301,
    };
    settings.custom_code = settings_data.custom_code || {
      head: "",
      body_start: "",
      body_end: "",
    };
    settings.analytics = settings_data.analytics || {
      google_analytics_id: "",
      baidu_tongji_id: "",
    };

    // 将重定向源域名数组拼接为按换行分隔的文本
    if (Array.isArray(settings.domain_redirects.redirect_from)) {
      redirectFromText = settings.domain_redirects.redirect_from.join("\n");
    } else {
      redirectFromText = "";
    }

    // 默认活跃语言紧跟基础语种走
    activeLocale = siteBase.default_locale;
  });

  // ==================== 标题管理 ====================
  $effect(() => {
    const title = t.title;
    appStore.setTitle(title);

    if (typeof document !== "undefined") {
      document.title = `${title} - ${t.brand_suffix || "你的应用"}`;
    }
  });

  // ==================== 多语言结构安全初始化 ====================
  $effect(() => {
    if (settings.header && !settings.header[activeLocale]) {
      settings.header[activeLocale] = {
        logo: { type: "text", text: "", url: "" },
        menu_items: [],
        cta_button: { show: false, text: "", link: "" },
      };
    } else if (settings.header && settings.header[activeLocale]) {
      if (!settings.header[activeLocale].logo) {
        settings.header[activeLocale].logo = {
          type: "text",
          text: "",
          url: "",
        };
      }
      if (!settings.header[activeLocale].cta_button) {
        settings.header[activeLocale].cta_button = {
          show: false,
          text: "",
          link: "",
        };
      }
    }

    if (settings.footer && !settings.footer[activeLocale]) {
      settings.footer[activeLocale] = {
        copyright: "",
        icp_number: "",
        public_security_number: "",
        social_links: [],
        bottom_columns: [],
      };
    }
    if (settings.seo_metadata && !settings.seo_metadata[activeLocale]) {
      settings.seo_metadata[activeLocale] = {
        title: "",
        description: "",
        keywords: "",
      };
    }
  });

  // ==================== 菜单操作 ====================
  function addMenuItem() {
    if (!settings.header[activeLocale]) return;
    settings.header[activeLocale].menu_items = [
      ...settings.header[activeLocale].menu_items,
      { title: "menu item", link: "/", is_external: false, new_tab: false },
    ];
  }

  function removeMenuItem(index: number) {
    if (!settings.header[activeLocale]) return;
    settings.header[activeLocale].menu_items = settings.header[
      activeLocale
    ].menu_items.filter((_, i) => i !== index);
  }

  // ==================== Footer 操作 ====================
  function addSocialLink() {
    if (!settings.footer[activeLocale]) return;
    settings.footer[activeLocale].social_links = [
      ...settings.footer[activeLocale].social_links,
      { platform: "GitHub", url: "https://github.com" },
    ];
  }

  function removeSocialLink(index: number) {
    if (!settings.footer[activeLocale]) return;
    settings.footer[activeLocale].social_links = settings.footer[
      activeLocale
    ].social_links.filter((_, i) => i !== index);
  }

  function addFooterColumn() {
    if (!settings.footer[activeLocale]) return;
    settings.footer[activeLocale].bottom_columns = [
      ...settings.footer[activeLocale].bottom_columns,
      { title: "新链接列", links: [] },
    ];
  }

  function removeFooterColumn(colIndex: number) {
    if (!settings.footer[activeLocale]) return;
    settings.footer[activeLocale].bottom_columns = settings.footer[
      activeLocale
    ].bottom_columns.filter((_, i) => i !== colIndex);
  }

  function addSubLink(colIndex: number) {
    if (!settings.footer[activeLocale]) return;
    const updatedCols = [...settings.footer[activeLocale].bottom_columns];
    updatedCols[colIndex].links = [
      ...updatedCols[colIndex].links,
      { label: "子链接", url: "/" },
    ];
    settings.footer[activeLocale].bottom_columns = updatedCols;
  }

  function removeSubLink(colIndex: number, linkIndex: number) {
    if (!settings.footer[activeLocale]) return;
    const updatedCols = [...settings.footer[activeLocale].bottom_columns];
    updatedCols[colIndex].links = updatedCols[colIndex].links.filter(
      (_, i) => i !== linkIndex,
    );
    settings.footer[activeLocale].bottom_columns = updatedCols;
  }

  // ==================== 保存 ====================
  function handleSave() {
    if (!siteBase.subdomain || !siteBase.subdomain.trim()) {
      toast.error(t.form?.subdomain_required || "二级域名不能为空");
      return;
    }

    // 提交前解析文本框，拆分成 clean 域名数组
    const parsedRedirectFrom = redirectFromText
      .split(/[\n,]+/)
      .map((item) => item.trim())
      .filter((item) => item.length > 0);

    settings.domain_redirects.redirect_from = parsedRedirectFrom;

    router.patch(
      `/site`,
      {
        site: siteBase,
        settings: settings,
      },
      {
        onBefore: () =>
          toast.loading(t.form?.saving || "正在保存配置...", {
            id: "save-form-site",
          }),
        onSuccess: () =>
          toast.success(t.form?.save_success || "站点设置保存成功！", {
            id: "save-form-site",
          }),
        onError: (errors) => {
          const msg =
            Object.values(errors).join(", ") || "保存失败，请检查输入";
          toast.error(msg, { id: "save-form-site" });
        },
      },
    );
  }
</script>

<!-- 以下模板部分基本保持不变，仅做少量清理 -->
<div class="flex flex-col h-screen bg-slate-50 dark:bg-slate-950">
  <!-- 头部操作条 -->
  <header
    class="h-16 border-b flex items-center justify-between px-6 bg-white dark:bg-zinc-900 shadow-sm z-10"
  >
    <div class="flex items-center gap-4">
      <div>
        <h1 class="text-sm font-bold flex items-center gap-2">
          <Globe class="size-4 text-primary" />

          {siteBase.name}
        </h1>
      </div>
    </div>

    <div class="flex items-center gap-3">
      <button
        onclick={handleSave}
        class="flex items-center gap-2 px-6 py-1.5 text-xs font-bold bg-primary text-primary-foreground hover:bg-primary/90 rounded-md shadow-lg shadow-primary/20 transition-all"
      >
        <Save class="size-3" />
        {t.form?.save_settings || "保存设置"}
      </button>
    </div>
  </header>

  <div class="flex-1 flex overflow-hidden">
    <!-- 左侧分组侧边栏 -->
    <aside
      class="w-64 border-r bg-white dark:bg-zinc-900 p-4 flex flex-col gap-1.5"
    >
      <h2
        class="text-[11px] font-bold text-zinc-400 dark:text-zinc-500 uppercase tracking-wider px-3 mb-2"
      >
        {t.form?.config_groups || "配置分组"}
      </h2>

      <button
        onclick={() => (activeGroup = "basic")}
        class="flex items-center gap-3 px-3 py-2.5 text-xs font-semibold rounded-lg transition-all text-left {activeGroup ===
        'basic'
          ? 'bg-primary/10 text-primary'
          : 'hover:bg-slate-50 dark:hover:bg-zinc-800 text-zinc-600 dark:text-zinc-300'}"
      >
        <Settings class="size-4" />
        {t.form?.basic_title || "基础配置与域名"}
      </button>

      <button
        onclick={() => (activeGroup = "appearance")}
        class="flex items-center gap-3 px-3 py-2.5 text-xs font-semibold rounded-lg transition-all text-left {activeGroup ===
        'appearance'
          ? 'bg-primary/10 text-primary'
          : 'hover:bg-slate-50 dark:hover:bg-zinc-800 text-zinc-600 dark:text-zinc-300'}"
      >
        <Palette class="size-4" />
        {t.form?.appearance_title || "外观主题"}
      </button>

      <button
        onclick={() => (activeGroup = "navigation")}
        class="flex items-center gap-3 px-3 py-2.5 text-xs font-semibold rounded-lg transition-all text-left {activeGroup ===
        'navigation'
          ? 'bg-primary/10 text-primary'
          : 'hover:bg-slate-50 dark:hover:bg-zinc-800 text-zinc-600 dark:text-zinc-300'}"
      >
        <Navigation class="size-4" />
        {t.form?.navigation_title || "Header 导航菜单"}
      </button>

      <button
        onclick={() => (activeGroup = "footer")}
        class="flex items-center gap-3 px-3 py-2.5 text-xs font-semibold rounded-lg transition-all text-left {activeGroup ===
        'footer'
          ? 'bg-primary/10 text-primary'
          : 'hover:bg-slate-50 dark:hover:bg-zinc-800 text-zinc-600 dark:text-zinc-300'}"
      >
        <Shield class="size-4" />
        {t.form?.footer_title || "Footer 与版权备案"}
      </button>

      <button
        onclick={() => (activeGroup = "seo")}
        class="flex items-center gap-3 px-3 py-2.5 text-xs font-semibold rounded-lg transition-all text-left {activeGroup ===
        'seo'
          ? 'bg-primary/10 text-primary'
          : 'hover:bg-slate-50 dark:hover:bg-zinc-800 text-zinc-600 dark:text-zinc-300'}"
      >
        <Search class="size-4" />
        {t.form?.seo_title || "SEO 搜索引擎优化"}
      </button>

      <button
        onclick={() => (activeGroup = "analytics")}
        class="flex items-center gap-3 px-3 py-2.5 text-xs font-semibold rounded-lg transition-all text-left {activeGroup ===
        'analytics'
          ? 'bg-primary/10 text-primary'
          : 'hover:bg-slate-50 dark:hover:bg-zinc-800 text-zinc-600 dark:text-zinc-300'}"
      >
        <BarChart3 class="size-4" />
        {t.form?.analytics_title || "追踪代码与分析"}
      </button>
    </aside>

    <!-- 中间主表单区域 -->
    <main class="flex-1 overflow-y-auto p-8 max-w-4xl mx-auto">
      <!-- 只有需要多语言渲染的分组，才在头部展示语言切换器 -->
      {#if ["navigation", "footer", "seo"].includes(activeGroup)}
        <div
          class="mb-6 p-4 bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-900/30 rounded-xl flex items-center justify-between"
        >
          <div
            class="flex items-center gap-2 text-xs text-yellow-800 dark:text-yellow-300"
          >
            <Globe class="size-4" />
            <span
              >{t.form?.select_locale?.replace(
                "%{locale}",
                activeLocale.toUpperCase(),
              ) ||
                `您当前正在编辑 ${activeLocale.toUpperCase()} 语言环境下的定制内容。`}</span
            >
          </div>
          <div
            class="flex gap-1 bg-white dark:bg-zinc-900 p-1 rounded-lg border"
          >
            {#each siteBase.supported_locales as loc}
              <button
                onclick={() => (activeLocale = loc)}
                class="px-3 py-1 text-xs font-bold rounded-md transition-all {activeLocale ===
                loc
                  ? 'bg-primary text-white'
                  : 'hover:bg-slate-100 dark:hover:bg-zinc-800 text-zinc-600 dark:text-zinc-400'}"
              >
                {loc.toUpperCase()}
              </button>
            {/each}
          </div>
        </div>
      {/if}

      <!-- 1. 基础配置 -->
      {#if activeGroup === "basic"}
        <section
          class="space-y-6 bg-white dark:bg-zinc-900 p-8 rounded-xl border shadow-sm"
        >
          <h3
            class="text-base font-bold text-zinc-950 dark:text-white border-b pb-3"
          >
            {t.form?.basic_title || "基础配置与域名"}
          </h3>

          <div class="grid grid-cols-2 gap-6">
            <div class="space-y-1.5">
              <label class="text-xs font-bold text-zinc-500"
                >{t.form?.site_name || "站点名称"}</label
              >
              <input
                type="text"
                bind:value={siteBase.name}
                class="input-theme"
              />
            </div>
            <!-- <div class="space-y-1.5">
              <label class="text-xs font-bold text-zinc-500">{t.form?.subdomain || "二级域名 (Subdomain)"}</label>
              <input type="text" bind:value={siteBase.subdomain} class="input-theme" readonly/>
            </div> -->
          </div>

          <!-- 域名自解析绑定组件 -->
          <div
            class="bg-slate-50 dark:bg-zinc-950 p-5 rounded-lg border space-y-4"
          >
            <h4
              class="text-xs font-bold text-zinc-700 dark:text-zinc-300 flex items-center gap-2"
            >
              <Network class="size-4 text-primary" />
              {t.form?.domains || "自解析域名设置"}
            </h4>

            <!-- 提示文案区域 -->
            <div
              class="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200/60 dark:border-amber-800/40 rounded-md text-amber-800 dark:text-amber-300 text-xs leading-relaxed"
            >
              <Info
                class="size-4 shrink-0 mt-0.5 text-amber-600 dark:text-amber-400"
              />
              <span
                >{t.form?.domain_bind_tip ||
                  "如果您需要绑定自定义域名，请联系客服人员进行手动配置与绑定。"}</span
              >
            </div>

            <div class="grid grid-cols-1 gap-4">
              <!-- 主域名绑定 -->
              <div class="space-y-1.5">
                <label class="text-[11px] text-zinc-400 font-bold"
                  >{t.form?.primary_domain || "主域名 (Primary Domain)"}</label
                >
                <input
                  type="text"
                  bind:value={settings.domain_redirects.primary}
                  placeholder="e.g. www.yourdomain.com"
                  class="input-theme bg-white dark:bg-zinc-900"
                />
              </div>

              <!-- 副域名 / 重定向来源域名绑定 -->
              <div class="space-y-1.5">
                <label class="text-[11px] text-zinc-400 font-bold"
                  >{t.form?.sub_domains ||
                    "副域名 / 重定向域名 (Redirect From)"}</label
                >
                <textarea
                  bind:value={redirectFromText}
                  placeholder="例如: yourdomain.com, m.yourdomain.com (每行一个或逗号分隔)"
                  rows="3"
                  class="input-theme bg-white dark:bg-zinc-900 resize-none font-mono text-xs"
                ></textarea>
              </div>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-6">
            <div class="space-y-1.5">
              <label class="text-xs font-bold text-zinc-500"
                >{t.form?.default_locale || "默认主语种"}</label
              >
              <select bind:value={siteBase.default_locale} class="input-theme">
                {#each WORLD_LANGUAGES as lang}
                  <option value={lang.code}>{lang.name} ({lang.code})</option>
                {/each}
              </select>
            </div>
            <div class="space-y-1.5">
              <label class="text-xs font-bold text-zinc-500"
                >{t.form?.supported_locales ||
                  "已启用语种多选（按住 Ctrl/Cmd 多选）"}</label
              >
              <select
                multiple
                bind:value={siteBase.supported_locales}
                class="input-theme h-32 scrollbar-thin"
              >
                {#each WORLD_LANGUAGES as lang}
                  <option value={lang.code}>{lang.name} ({lang.code})</option>
                {/each}
              </select>
            </div>
          </div>
        </section>
      {/if}

      <!-- 2. 外观设置 -->
      {#if activeGroup === "appearance"}
        <section
          class="space-y-6 bg-white dark:bg-zinc-900 p-8 rounded-xl border shadow-sm"
        >
          <h3
            class="text-base font-bold text-zinc-950 dark:text-white border-b pb-3"
          >
            {t.form?.appearance_title || "外观主题"}
          </h3>

          <div class="grid grid-cols-2 gap-6">
            <div class="space-y-1.5">
              <label class="text-xs font-bold text-zinc-500"
                >{t.form?.theme_mode || "默认主题模式"}</label
              >
              <select
                bind:value={settings.appearance.theme}
                class="input-theme"
              >
                <option value="light"
                  >{t.form?.theme_light || "明亮模式 (Light)"}</option
                >
                <option value="dark"
                  >{t.form?.theme_dark || "暗黑模式 (Dark)"}</option
                >
              </select>
            </div>
            <div class="space-y-1.5">
              <label class="text-xs font-bold text-zinc-500"
                >{t.form?.primary_color ||
                  "系统主题高亮色 (Primary Color)"}</label
              >
              <div class="flex gap-2">
                <input
                  type="color"
                  bind:value={settings.appearance.primary_color}
                  class="size-10 p-1 border rounded-lg cursor-pointer bg-transparent"
                />
                <input
                  type="text"
                  bind:value={settings.appearance.primary_color}
                  class="input-theme flex-1"
                />
              </div>
            </div>
          </div>

          <div class="space-y-1.5">
            <label class="text-xs font-bold text-zinc-500"
              >{t.form?.favicon_url || "Favicon 图标地址 (URL)"}</label
            >
            <input
              type="text"
              bind:value={settings.appearance.favicon_url}
              placeholder="https://..."
              class="input-theme"
            />
          </div>
        </section>
      {/if}

      <!-- 3. Header 导航 (多语言) -->
      {#if activeGroup === "navigation" && settings.header[activeLocale]}
        <section
          class="space-y-6 bg-white dark:bg-zinc-900 p-8 rounded-xl border shadow-sm"
        >
          <h3
            class="text-base font-bold text-zinc-950 dark:text-white border-b pb-3"
          >
            {t.form?.header_config?.replace(
              "%{locale}",
              activeLocale.toUpperCase(),
            ) || `Header 配置 (${activeLocale.toUpperCase()})`}
          </h3>

          <!-- Logo 设置 -->
          <div
            class="bg-slate-50 dark:bg-zinc-950 p-4 rounded-lg space-y-4 border"
          >
            <h4 class="text-xs font-bold text-zinc-700 dark:text-zinc-300">
              {t.form?.logo_settings || "Logo 形象设置"}
            </h4>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div class="space-y-1">
                <label class="text-[10px] text-zinc-400 font-bold"
                  >{t.form?.logo_type || "展示模式"}</label
                >
                <select
                  bind:value={settings.header[activeLocale].logo.type}
                  class="input-theme !py-1"
                >
                  <option value="text"
                    >{t.form?.logo_type_text || "纯文字 Logo"}</option
                  >
                  <option value="image"
                    >{t.form?.logo_type_image || "纯图片 Logo"}</option
                  >
                  <option value="image_text"
                    >{t.form?.logo_type_both || "图片 + 文字"}</option
                  >
                </select>
              </div>
              <div class="space-y-1">
                <label class="text-[10px] text-zinc-400 font-bold"
                  >{t.form?.logo_text || "Logo 文本"}</label
                >
                <input
                  type="text"
                  bind:value={settings.header[activeLocale].logo.text}
                  class="input-theme !py-1"
                />
              </div>
              <div class="space-y-1">
                <label class="text-[10px] text-zinc-400 font-bold"
                  >{t.form?.logo_url || "Logo 图片地址"}</label
                >
                <input
                  type="text"
                  bind:value={settings.header[activeLocale].logo.url}
                  placeholder="https://..."
                  class="input-theme !py-1"
                />
              </div>
            </div>
          </div>

          <!-- CTA 行动按钮设置 -->
          <div
            class="bg-slate-50 dark:bg-zinc-950 p-4 rounded-lg space-y-4 border"
          >
            <div class="flex items-center justify-between">
              <h4 class="text-xs font-bold text-zinc-700 dark:text-zinc-300">
                {t.form?.cta_settings || "Header 行动按钮 (CTA Button)"}
              </h4>
              <label class="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  bind:checked={settings.header[activeLocale].cta_button.show}
                  class="rounded text-primary border-zinc-300 focus:ring-primary size-4"
                />
                <span class="text-xs font-bold text-zinc-600 dark:text-zinc-400"
                  >{t.form?.cta_enable || "启用按钮"}</span
                >
              </label>
            </div>

            {#if settings.header[activeLocale].cta_button.show}
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                <div class="space-y-1">
                  <label class="text-[10px] text-zinc-400 font-bold"
                    >{t.form?.cta_text || "按钮文本"}</label
                  >
                  <input
                    type="text"
                    bind:value={settings.header[activeLocale].cta_button.text}
                    placeholder={t.form?.cta_text_placeholder ||
                      "如：立即咨询、免费试用"}
                    class="input-theme !py-1.5 bg-white dark:bg-zinc-900"
                  />
                </div>
                <div class="space-y-1">
                  <label class="text-[10px] text-zinc-400 font-bold"
                    >{t.form?.cta_link || "按钮跳转链接"}</label
                  >
                  <input
                    type="text"
                    bind:value={settings.header[activeLocale].cta_button.link}
                    placeholder={t.form?.cta_link_placeholder ||
                      "/contact 或 https://..."}
                    class="input-theme !py-1.5 bg-white dark:bg-zinc-900"
                  />
                </div>
              </div>
            {/if}
          </div>

          <!-- 导航菜单链接 -->
          <div class="space-y-4">
            <div class="flex items-center justify-between">
              <h4 class="text-xs font-bold text-zinc-700 dark:text-zinc-300">
                {t.form?.nav_menu || "导航菜单链接"}
              </h4>
              <button
                onclick={addMenuItem}
                class="flex items-center gap-1 text-[10px] text-primary hover:underline font-bold"
              >
                <Plus class="size-3" />
                {t.form?.add_menu || "添加菜单项"}
              </button>
            </div>

            <div class="space-y-3">
              {#each settings.header[activeLocale].menu_items as item, index}
                <div
                  class="flex gap-4 items-start bg-slate-50 dark:bg-zinc-950 p-4 rounded-xl border relative group/item"
                >
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4 flex-1">
                    <div class="space-y-1">
                      <label class="text-[9px] text-zinc-400 font-semibold"
                        >{t.form?.menu_text || "显示文本"}</label
                      >
                      <!-- 绑定键已更改为 item.title -->
                      <input
                        type="text"
                        bind:value={item.title}
                        placeholder={t.form?.text_placeholder}
                        class="input-theme !py-1.5 bg-white dark:bg-zinc-900"
                      />
                    </div>
                    <div class="space-y-1">
                      <label class="text-[9px] text-zinc-400 font-semibold"
                        >{t.form?.menu_link || "跳转路由"}</label
                      >
                      <input
                        type="text"
                        bind:value={item.link}
                        placeholder={t.form?.url_placeholder}
                        class="input-theme !py-1.5 bg-white dark:bg-zinc-900"
                      />
                    </div>
                  </div>

                  <!-- 是否外链与新窗口打开 -->
                  <div class="flex flex-col gap-2 justify-center pt-5">
                    <label
                      class="flex items-center gap-1.5 text-[10px] font-bold text-zinc-500 cursor-pointer select-none"
                    >
                      <input
                        type="checkbox"
                        bind:checked={item.is_external}
                        class="rounded text-primary border-zinc-300 focus:ring-primary size-3.5"
                      />
                      <span>{t.form?.menu_is_external || "外链"}</span>
                    </label>
                    <label
                      class="flex items-center gap-1.5 text-[10px] font-bold text-zinc-500 cursor-pointer select-none"
                    >
                      <input
                        type="checkbox"
                        bind:checked={item.new_tab}
                        class="rounded text-primary border-zinc-300 focus:ring-primary size-3.5"
                      />
                      <span>{t.form?.menu_new_tab || "新窗口"}</span>
                    </label>
                  </div>

                  <button
                    onclick={() => removeMenuItem(index)}
                    class="text-red-500 hover:text-red-700 p-2 mt-4 hover:bg-red-50 dark:hover:bg-red-950/20 rounded-lg transition-colors"
                  >
                    <Trash2 class="size-4" />
                  </button>
                </div>
              {/each}
            </div>
          </div>
        </section>
      {/if}

      <!-- 4. Footer & 备案 & 列链接 -->
      {#if activeGroup === "footer" && settings.footer[activeLocale]}
        <section
          class="space-y-6 bg-white dark:bg-zinc-900 p-8 rounded-xl border shadow-sm"
        >
          <h3
            class="text-base font-bold text-zinc-950 dark:text-white border-b pb-3"
          >
            {t.form?.footer_title || "Footer 与版权备案"} ({activeLocale.toUpperCase()})
          </h3>

          <div class="space-y-1.5">
            <label class="text-xs font-bold text-zinc-500"
              >{t.form?.copyright || "版权文案 (Copyright)"}</label
            >
            <input
              type="text"
              bind:value={settings.footer[activeLocale].copyright}
              class="input-theme"
            />
          </div>

          <!-- ICP & 公安网备案（中文环境专有） -->
          {#if activeLocale === "zh"}
            <div
              class="grid grid-cols-2 gap-6 bg-blue-50/50 dark:bg-blue-950/10 p-4 border border-blue-100 dark:border-zinc-800 rounded-lg"
            >
              <div class="space-y-1.5">
                <label
                  class="text-xs font-bold text-blue-800 dark:text-blue-300"
                  >{t.form?.icp_number || "ICP 备案号"}</label
                >
                <input
                  type="text"
                  bind:value={settings.footer[activeLocale].icp_number}
                  placeholder="京ICP备2021036370号"
                  class="input-theme bg-white dark:bg-zinc-900"
                />
              </div>
              <div class="space-y-1.5">
                <label
                  class="text-xs font-bold text-blue-800 dark:text-blue-300"
                  >{t.form?.security_number || "公安网备号"}</label
                >
                <input
                  type="text"
                  bind:value={
                    settings.footer[activeLocale].public_security_number
                  }
                  placeholder="京公网安备xxxxxxxx号"
                  class="input-theme bg-white dark:bg-zinc-900"
                />
              </div>
            </div>
          {/if}

          <!-- 社交链接 (social_links) 动态维护 -->
          <div class="space-y-4 border-t pt-4">
            <div class="flex items-center justify-between">
              <h4
                class="text-xs font-bold text-zinc-700 dark:text-zinc-300 flex items-center gap-1.5"
              >
                <Link2 class="size-4 text-primary" />
                {t.form?.social_links || "社交媒体链接"}
              </h4>
              <button
                onclick={addSocialLink}
                class="flex items-center gap-1 text-[10px] text-primary hover:underline font-bold"
              >
                <Plus class="size-3" />
                {t.form?.add_social || "添加社交链接"}
              </button>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              {#each settings.footer[activeLocale].social_links as soc, index}
                <div
                  class="flex gap-2 items-center bg-slate-50 dark:bg-zinc-950 p-2.5 rounded-lg border"
                >
                  <select
                    bind:value={soc.platform}
                    class="input-theme !py-1 !text-xs w-32 bg-white dark:bg-zinc-900"
                  >
                    <option value="WeChat">WeChat</option>
                    <option value="GitHub">GitHub</option>
                    <option value="Twitter">Twitter / X</option>
                    <option value="LinkedIn">LinkedIn</option>
                    <option value="Facebook">Facebook</option>
                  </select>
                  <input
                    type="text"
                    bind:value={soc.url}
                    placeholder="链接地址"
                    class="input-theme !py-1 !text-xs bg-white dark:bg-zinc-900"
                  />
                  <button
                    onclick={() => removeSocialLink(index)}
                    class="text-red-500 hover:text-red-700 p-1"
                  >
                    <Trash2 class="size-4" />
                  </button>
                </div>
              {/each}
            </div>
          </div>

          <!-- Footer 底部多列导航 (bottom_columns) 深度配置 -->
          <div class="space-y-4 border-t pt-4">
            <div class="flex items-center justify-between">
              <h4
                class="text-xs font-bold text-zinc-700 dark:text-zinc-300 flex items-center gap-1.5"
              >
                <ExternalLink class="size-4 text-primary" />
                {t.form?.footer_columns || "Footer 底部链接列"}
              </h4>
              <button
                onclick={addFooterColumn}
                class="flex items-center gap-1 text-[10px] text-primary hover:underline font-bold"
              >
                <Plus class="size-3" />
                {t.form?.add_column || "添加列"}
              </button>
            </div>

            <div class="space-y-6">
              {#each settings.footer[activeLocale].bottom_columns as col, colIndex}
                <div
                  class="p-4 bg-slate-50 dark:bg-zinc-950/60 border rounded-xl space-y-4 relative"
                >
                  <div class="flex items-center justify-between">
                    <div class="space-y-1 flex-1 max-w-sm">
                      <label class="text-[10px] text-zinc-400 font-bold"
                        >{t.form?.column_title || "列标题"}</label
                      >
                      <input
                        type="text"
                        bind:value={col.title}
                        class="input-theme !py-1 bg-white dark:bg-zinc-900 font-semibold"
                      />
                    </div>
                    <button
                      onclick={() => removeFooterColumn(colIndex)}
                      class="text-red-500 hover:text-red-700 p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/20"
                    >
                      <Trash2 class="size-4" />
                    </button>
                  </div>

                  <!-- 这一列下的子链接 -->
                  <div class="pl-4 border-l-2 border-primary/30 space-y-2">
                    <div class="flex items-center justify-between">
                      <span class="text-[10px] text-zinc-400 font-semibold"
                        >{t.form?.link_subitems || "链接子项 (Links)"}</span
                      >
                      <button
                        onclick={() => addSubLink(colIndex)}
                        class="text-[9px] text-primary hover:underline font-bold flex items-center"
                      >
                        <Plus class="size-2.5" />
                        {t.form?.add_column_link || "添加链接"}
                      </button>
                    </div>

                    <div class="space-y-2">
                      {#each col.links as link, linkIndex}
                        <div
                          class="flex gap-2 items-center bg-white dark:bg-zinc-900 p-2 rounded-lg border"
                        >
                          <input
                            type="text"
                            bind:value={link.label}
                            placeholder={t.form?.column_label || "标签"}
                            class="input-theme !py-1 !text-xs"
                          />
                          <input
                            type="text"
                            bind:value={link.url}
                            placeholder="URL"
                            class="input-theme !py-1 !text-xs"
                          />
                          <button
                            onclick={() => removeSubLink(colIndex, linkIndex)}
                            class="text-red-500 hover:text-red-700 p-1"
                          >
                            <Trash2 class="size-3.5" />
                          </button>
                        </div>
                      {/each}
                    </div>
                  </div>
                </div>
              {/each}
            </div>
          </div>
        </section>
      {/if}

      <!-- 5. SEO 设置 (多语言) -->
      {#if activeGroup === "seo" && settings.seo_metadata[activeLocale]}
        <section
          class="space-y-6 bg-white dark:bg-zinc-900 p-8 rounded-xl border shadow-sm"
        >
          <h3
            class="text-base font-bold text-zinc-950 dark:text-white border-b pb-3"
          >
            {t.form?.seo_config?.replace(
              "%{locale}",
              activeLocale.toUpperCase(),
            ) || `SEO 元数据 (${activeLocale.toUpperCase()})`}
          </h3>

          <div class="space-y-1.5">
            <label class="text-xs font-bold text-zinc-500"
              >{t.form?.meta_title || "页面标题 (Meta Title)"}</label
            >
            <input
              type="text"
              bind:value={settings.seo_metadata[activeLocale].title}
              class="input-theme"
            />
          </div>

          <div class="space-y-1.5">
            <label class="text-xs font-bold text-zinc-500"
              >{t.form?.meta_description ||
                "页面描述 (Meta Description)"}</label
            >
            <textarea
              bind:value={settings.seo_metadata[activeLocale].description}
              rows="3"
              class="input-theme resize-none"
            ></textarea>
          </div>

          <div class="space-y-1.5">
            <label class="text-xs font-bold text-zinc-500"
              >{t.form?.meta_keywords ||
                "页面关键词 (Keywords - 逗号分隔)"}</label
            >
            <input
              type="text"
              bind:value={settings.seo_metadata[activeLocale].keywords}
              class="input-theme"
            />
          </div>
        </section>
      {/if}

      <!-- 6. 分析与追踪 -->
      {#if activeGroup === "analytics"}
        <section
          class="space-y-6 bg-white dark:bg-zinc-900 p-8 rounded-xl border shadow-sm"
        >
          <h3
            class="text-base font-bold text-zinc-950 dark:text-white border-b pb-3"
          >
            {t.form?.analytics_title || "追踪代码与分析"}
          </h3>

          <div class="grid grid-cols-2 gap-6">
            <div class="space-y-1.5">
              <label class="text-xs font-bold text-zinc-500"
                >{t.form?.google_analytics_id || "Google Analytics ID"}</label
              >
              <input
                type="text"
                bind:value={settings.analytics.google_analytics_id}
                placeholder="G-XXXXXXXXXX"
                class="input-theme"
              />
            </div>
            <div class="space-y-1.5">
              <label class="text-xs font-bold text-zinc-500"
                >{t.form?.baidu_tongji_id || "百度统计 ID"}</label
              >
              <input
                type="text"
                bind:value={settings.analytics.baidu_tongji_id}
                placeholder="xxxxxxxxxx"
                class="input-theme"
              />
            </div>
          </div>

          <!--
          <h3 class="text-base font-bold text-zinc-950 dark:text-white border-b pb-3 pt-4">
            {t.form?.custom_code_title || "自定义插入代码"}
          </h3>
          <div class="space-y-4">
            <div class="space-y-1.5">
              <label class="text-xs font-bold text-zinc-500">{t.form?.head_custom_code || "Head 自定义代码（注入统计脚本或 CSS）"}</label>
              <textarea bind:value={settings.custom_code.head} placeholder={t.form?.head_code_placeholder || "<script>...</script>"} rows="4" class="input-theme font-mono text-xs"></textarea>
            </div>
          </div>
          -->
        </section>
      {/if}
    </main>
  </div>
</div>

<style>
  /* 极光美学表单组件 */
  .input-theme {
    width: 100%;
    padding: 0.625rem 0.875rem;
    font-size: 0.8125rem;
    background-color: transparent;
    border: 1px solid #e2e8f0;
    border-radius: 0.5rem;
    outline: none;
    transition: all 0.15s ease-in-out;
  }
  :global(.dark) .input-theme {
    border-color: #27272a;
    background-color: #09090b;
    color: #f4f4f5;
  }
  .input-theme:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
  }
  .scrollbar-thin::-webkit-scrollbar {
    width: 6px;
  }
  .scrollbar-thin::-webkit-scrollbar-thumb {
    background-color: #cbd5e1;
    border-radius: 3px;
  }
  :global(.dark) .scrollbar-thin::-webkit-scrollbar-thumb {
    background-color: #27272a;
  }
</style>
